import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Send, MessageCircle, Facebook, Instagram, Twitter, Linkedin } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { useSite } from '../context/SiteContext';
import { getText } from '../lib/siteContent';
import api from '../lib/api';

const VALID_SUBJECTS = ['general', 'buying', 'selling', 'renting', 'agent'];
const SUBJECT_MESSAGES = { agent: 'I want to become an Advisor.' };

export default function Contact() {
  const { toast } = useToast();
  const { settings } = useSite();
  const [searchParams] = useSearchParams();
  const t = (key) => getText(settings, key);
  const paramSubject = searchParams.get('subject');
  const initialSubject = VALID_SUBJECTS.includes(paramSubject) ? paramSubject : 'general';
  const c = {
    address: settings?.address || '',
    phone: settings?.phone || '',
    mobile: settings?.mobile || '',
    email: settings?.email || '',
    facebook: settings?.facebook || '#',
    instagram: settings?.instagram || '#',
    twitter: settings?.twitter || '#',
    linkedin: settings?.linkedin || '#'
  };
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: initialSubject, message: SUBJECT_MESSAGES[initialSubject] || '' });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/inquiries', form);
      toast({ title: 'Message sent!', description: 'Our team will respond within 24 hours.' });
      setForm({ name: '', email: '', phone: '', subject: 'general', message: '' });
    } catch {
      toast({ title: 'Failed to send', description: 'Please try again later.', variant: 'destructive' });
    }
  };

  return (
    <div className="bg-white">
      <section className="bg-gradient-to-r from-neutral-900 to-neutral-800 text-white">
        <div className="max-w-7xl mx-auto px-6 py-14">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#DC2626]">Contact Us</span>
          <h1 className="text-3xl md:text-5xl font-bold mt-1">{t('contact_hero_title')}</h1>
          <p className="mt-3 text-neutral-300 max-w-2xl">{t('contact_hero_subtitle')}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1 space-y-4">
          {c.address && (
            <div className="p-6 rounded-xl border border-neutral-200 bg-white">
              <MapPin className="w-7 h-7 text-[#DC2626] mb-2" />
              <h3 className="font-bold text-neutral-900">Office Address</h3>
              <p className="text-sm text-neutral-600 mt-1">{c.address}</p>
            </div>
          )}
          {(c.phone || c.mobile) && (
            <div className="p-6 rounded-xl border border-neutral-200 bg-white">
              <Phone className="w-7 h-7 text-[#DC2626] mb-2" />
              <h3 className="font-bold text-neutral-900">Phone</h3>
              <div className="text-sm text-neutral-600 mt-1">
                {c.phone && <a href={'tel:' + c.phone} className="block hover:text-[#DC2626]">{c.phone}</a>}
                {c.mobile && <a href={'tel:' + c.mobile} className="block hover:text-[#DC2626]">{c.mobile}</a>}
              </div>
            </div>
          )}
          {c.email && (
            <div className="p-6 rounded-xl border border-neutral-200 bg-white">
              <Mail className="w-7 h-7 text-[#DC2626] mb-2" />
              <h3 className="font-bold text-neutral-900">Email</h3>
              <a href={'mailto:' + c.email} className="text-sm text-neutral-600 mt-1 hover:text-[#DC2626]">{c.email}</a>
            </div>
          )}
          <div className="p-6 rounded-xl border border-neutral-200 bg-white">
            <Clock className="w-7 h-7 text-[#DC2626] mb-2" />
            <h3 className="font-bold text-neutral-900">Office Hours</h3>
            <p className="text-sm text-neutral-600 mt-1 whitespace-pre-line">{t('contact_hours')}</p>
          </div>
          <div className="p-6 rounded-xl border border-neutral-200 bg-white">
            <h3 className="font-bold text-neutral-900 mb-3">Follow Us</h3>
            <div className="flex gap-2">
              <a href={c.facebook} className="w-9 h-9 rounded-full bg-neutral-100 hover:bg-[#DC2626] hover:text-white flex items-center justify-center transition-colors"><Facebook className="w-4 h-4" /></a>
              <a href={c.instagram} className="w-9 h-9 rounded-full bg-neutral-100 hover:bg-[#DC2626] hover:text-white flex items-center justify-center transition-colors"><Instagram className="w-4 h-4" /></a>
              <a href={c.twitter} className="w-9 h-9 rounded-full bg-neutral-100 hover:bg-[#DC2626] hover:text-white flex items-center justify-center transition-colors"><Twitter className="w-4 h-4" /></a>
              <a href={c.linkedin} className="w-9 h-9 rounded-full bg-neutral-100 hover:bg-[#DC2626] hover:text-white flex items-center justify-center transition-colors"><Linkedin className="w-4 h-4" /></a>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="p-8 rounded-xl border border-neutral-200 bg-white">
            <div className="flex items-center gap-2 mb-1">
              <MessageCircle className="w-6 h-6 text-[#DC2626]" />
              <h2 className="text-2xl font-bold text-neutral-900">{t('contact_form_title')}</h2>
            </div>
            <p className="text-sm text-neutral-600 mb-6">{t('contact_form_subtitle')}</p>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-neutral-700 mb-1 block">Full Name *</label>
                  <Input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Juan Dela Cruz" />
                </div>
                <div>
                  <label className="text-sm font-medium text-neutral-700 mb-1 block">Email *</label>
                  <Input required type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="you@example.com" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-neutral-700 mb-1 block">Phone Number</label>
                  <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="+63 9XX XXX XXXX" />
                </div>
                <div>
                  <label className="text-sm font-medium text-neutral-700 mb-1 block">Subject</label>
                  <Select value={form.subject} onValueChange={(v) => setForm({...form, subject: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General Inquiry</SelectItem>
                      <SelectItem value="buying">Looking to Buy</SelectItem>
                      <SelectItem value="selling">Looking to Sell</SelectItem>
                      <SelectItem value="renting">Looking to Rent</SelectItem>
                      <SelectItem value="agent">Become an Advisor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-neutral-700 mb-1 block">Your Message *</label>
                <Textarea required rows={6} value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} placeholder="Tell us how we can help..." />
              </div>
              <Button type="submit" className="btn-primary gap-2 h-11 px-6"><Send className="w-4 h-4" /> Send Message</Button>
            </form>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-16">
        <div className="rounded-xl overflow-hidden border border-neutral-200">
          <iframe
            title="Office Map"
            src="https://maps.google.com/maps?q=Cebu%20City&t=&z=13&ie=UTF8&iwloc=&output=embed"
            width="100%"
            height="380"
            style={{border:0}}
            loading="lazy"
          />
        </div>
      </section>
    </div>
  );
}
